import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  Typography,
  Icon,
  Option,
  Button,
  Card,
  Skeleton,
  Tooltip,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { EmployeeFullInfo, getEmployeeInfo } from 'src/api/bookings';
import { EmployeePermission } from 'src/api/permissions';
import { BookingObjectAttribute } from 'src/components/BookingObjectAttribute/BookingObjectAttribute';
import { useConfirmationDialog } from 'src/components/ConfirmationDialog/useConfirmationDialog';
import { FormDurationSelect } from 'src/components/DurationSelect/FormDurationSelect';
import { FormMaskField } from 'src/components/FormMaskField/FormMaskField';
import { FormPersonAutocomplete } from 'src/components/FormPersonAutocomplete/FormPersonAutocomplete';
import { Loader } from 'src/components/Loader/Loader';
import { Section } from 'src/components/Section/Section';
import { messagesActions } from 'src/store/messages/slice';
import {
  getCurrentRoom,
  getRoomLocation,
  getRoomSlots,
  getRoomsDate,
  getRoomsTimePeriodLocal,
  getRoomsTown,
} from 'src/store/rooms/selectors';
import { unbookRoom } from 'src/store/rooms/services';
import { calculateRoomsTimeError } from 'src/store/rooms/utils';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getEmployeeNumber } from 'src/store/user/selectors';
import { BookingData, BookingState, FreePeriod, Room } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { findLeastRightTime } from 'src/utils/findLeastTime';
import { formatTime } from 'src/utils/formatTime';
import { getDateTime } from 'src/utils/getDateTime';
import { plural } from 'src/utils/plural';
import { splitTime } from 'src/utils/splitTime';
import { toAddress } from 'src/utils/toAddress';

import classes from './RoomBookingSidesheetPanel.module.scss';

const mask = {
  mask: 'HH:MM—HH:MM',
  blocks: {
    HH: {
      // @ts-expect-error global
      mask: IMask.MaskedRange,
      from: 0,
      to: 23,
      minLength: 2,
    },
    MM: {
      // @ts-expect-error global
      mask: IMask.MaskedRange,
      from: 0,
      to: 59,
      minLength: 2,
    },
  },
};

const split = (period: FreePeriod, intervalMs: number): FreePeriod[] => {
  const result: FreePeriod[] = [];

  const fromMs = +new Date(period.from);
  const toMs = +new Date(period.to);

  for (let i = fromMs; i < toMs; i += intervalMs) {
    const from = i;
    const to = from + intervalMs;

    if (to > toMs) break;

    result.push({
      from: new Date(from).toISOString(),
      to: new Date(to).toISOString(),
    });
  }

  return result;
};

interface FreeSlotProps {
  slot: FreePeriod;
}

const FreeSlot: FC<FreeSlotProps> = ({ slot }) => {
  const town = useSelector(getRoomsTown);

  const { change } = useForm();

  const time = useMemo(() => {
    const fromDate = getDateTime({
      date: new Date(slot.from),
      timezoneOffset: town?.timeZone,
    });
    const from = fromDate.toISOString();

    const toDate = getDateTime({
      date: new Date(slot.to),
      timezoneOffset: town?.timeZone,
    });
    const to = toDate.toISOString();

    return `${formatTime(new Date(from))} — ${formatTime(new Date(to))}`;
  }, [slot.from, slot.to, town?.timeZone]);

  const handleClick = useCallback(() => {
    change('time', time);
  }, [change, time]);

  return (
    <Button className={classes.free} onClick={handleClick}>
      {time}
    </Button>
  );
};

interface BusySlotProps {
  booking: BookingData;
  onUnbook: () => void;
  confirmation: () => Promise<unknown>;
  actual: boolean;
}

const BysySlot: FC<BusySlotProps> = ({
  booking,
  onUnbook,
  confirmation,
  actual,
}) => {
  const employeeNumber = useSelector(getEmployeeNumber);
  const dispatch = useAppDispatch();

  const unbook = useCallback(async () => {
    onUnbook();
    const confirmed = await confirmation();
    if (confirmed) {
      await dispatch(unbookRoom(booking));
    }
  }, [booking, confirmation, dispatch, onUnbook]);

  const town = useSelector(getRoomsTown);

  const time = useMemo(() => {
    const fromDate = getDateTime({
      date: new Date(booking.bookedFrom),
      timezoneOffset: town?.timeZone,
    });
    const from = fromDate.toISOString();

    const toDate = getDateTime({
      date: new Date(booking.bookedTo),
      timezoneOffset: town?.timeZone,
    });
    const to = toDate.toISOString();

    return `${formatTime(new Date(from))} — ${formatTime(new Date(to))}`;
  }, [booking.bookedFrom, booking.bookedTo, town?.timeZone]);

  const [loading, setLoading] = useState(
    !!booking.responsibleEmployeeNumber &&
      booking.state === BookingState.Accepted,
  );

  const [employee, setEmployee] = useState<EmployeeFullInfo | undefined>();

  const getEmployee = useCallback(async () => {
    try {
      if (
        !booking.responsibleEmployeeNumber ||
        booking.state !== BookingState.Accepted
      )
        return;

      setLoading(true);
      const response = await getEmployeeInfo(booking.responsibleEmployeeNumber);
      setEmployee(response);
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
    } finally {
      setLoading(false);
    }
  }, [booking.responsibleEmployeeNumber, booking.state, dispatch]);

  useEffect(() => {
    getEmployee();
  }, [getEmployee]);

  const title = useMemo(() => {
    switch (booking.state) {
      case BookingState.Accepted:
        return employee
          ? `${employee.lastName} ${employee.firstName[0]}.${employee.middleName[0]}.`
          : '';
      case BookingState.BookingInProgress:
        return 'Выполняется бронь';
      case BookingState.UnbookingInProgress:
        return 'Снимается бронь';
    }
  }, [booking.state, employee]);

  return (
    <Card className={classes.busy}>
      <div className={classes.busyInfo}>
        <Typography variant='subtitle3' className='inactive-text'>
          {time}
        </Typography>
        {loading ? (
          <Skeleton />
        ) : (
          <Typography variant='caption' className='inactive-text'>
            {title}
          </Typography>
        )}
      </div>
      {booking.state === BookingState.BookingInProgress ||
      booking.state === BookingState.UnbookingInProgress ? (
        <div>
          <Loader />
        </div>
      ) : employeeNumber === booking.responsibleEmployeeNumber && actual ? (
        <Tooltip title='Отменить'>
          <Button
            size='small'
            startIcon={<Icon iconName={Icons.Delete} />}
            onClick={unbook}
          />
        </Tooltip>
      ) : null}
    </Card>
  );
};

const Table: FC = () => {
  const room = useSelector(getCurrentRoom);
  const slots = useSelector(getRoomSlots);
  const date = useSelector(getRoomsDate);
  const tableInterval = useField<Option<string>>('tableInterval').input.value;

  const {
    dialog: unbookDialog,
    open: openUnbookDialog,
    confirmation,
  } = useConfirmationDialog({
    title: 'Отменить бронирование?',
    okLabel: 'Отменить',
    cancelLabel: 'Назад',
    text: 'После отмены бронирования это действие нельзя будет отменить. Чтобы создать новое бронирование, тебе нужно будет снова пройти весь процесс.',
  });

  const table = useMemo((): ReactNode[] => {
    const now = new Date();

    const result: ReactNode[] = [];

    const periods = [...slots.freePeriods, ...slots.bookedPeriods];
    periods.sort((first, second) => {
      const firstDate = +new Date(
        'bookedFrom' in first ? first.bookedFrom : first.from,
      );
      const secondDate = +new Date(
        'bookedFrom' in second ? second.bookedFrom : second.from,
      );

      return firstDate - secondDate;
    });

    for (const period of periods) {
      if ('bookedFrom' in period) {
        const toDate = +new Date(period.bookedTo);

        result.push(
          <BysySlot
            booking={period}
            key={`${period.bookedFrom}-${room?.mapPlaceId}-${tableInterval}-${date}`}
            onUnbook={openUnbookDialog}
            confirmation={confirmation}
            actual={toDate > +now}
          />,
        );
      } else {
        const fromDate = new Date(period.from);
        const toDate = new Date(period.to);
        if (+toDate < +now) continue;

        const least = findLeastRightTime(now, 15 * 60 * 1000);
        const adjustedPeriod: FreePeriod = {
          from: new Date(Math.max(+fromDate, +least)).toISOString(),
          to: period.to,
        };

        const splitted = split(adjustedPeriod, +tableInterval.id * 60 * 1000);
        for (const part of splitted) {
          result.push(
            <FreeSlot
              slot={part}
              key={`${part.from}-${room?.mapPlaceId}-${tableInterval}-${date}`}
            />,
          );
        }
      }
    }

    return result;
  }, [
    confirmation,
    date,
    openUnbookDialog,
    room?.mapPlaceId,
    slots.bookedPeriods,
    slots.freePeriods,
    tableInterval,
  ]);

  return table.length ? (
    <Section
      title='Расписание'
      testId='Section'
      contentClassName={classes.tableWrapper}
    >
      {unbookDialog}
      <FormDurationSelect name='tableInterval' />
      <div className={classes.tableContent}>{table}</div>
    </Section>
  ) : null;
};

export interface RoomBookingSidesheetFieldsProps {
  room?: Room;
  permissions: Set<EmployeePermission>;
}

export const RoomBookingSidesheetFields: FC<
  RoomBookingSidesheetFieldsProps
> = ({ room, permissions }) => {
  const { town, building, floor } = useSelector(getRoomLocation);

  const bookingData = toAddress({
    town: town?.townName,
    building: building?.address,
    floor: floor?.title,
  });

  const seatCount =
    room?.seatCount !== undefined
      ? `${room.seatCount} ${plural(room.seatCount, {
          zero: 'мест',
          one: 'место',
          two: 'места',
          few: 'места',
          many: 'мест',
          other: 'мест',
        })}`
      : null;

  const date = useSelector(getRoomsDate);
  const { bookedPeriods } = useSelector(getRoomSlots);
  const periodskey = useMemo(
    (): string =>
      bookedPeriods.map(p => `${p.bookedFrom}-${p.bookedTo}`).join('_') +
      room?.mapPlaceId,
    [bookedPeriods, room?.mapPlaceId],
  );
  const { from: settingsFrom } = useSelector(getRoomsTimePeriodLocal);

  const validateTime = useCallback(
    (value: string | undefined): string => {
      if (!value) return '';

      const [from, to] = value.split('—');

      const timeBoundariesError = calculateRoomsTimeError({
        date: settingsFrom,
        from,
        to,
        timezoneOffset: town?.timeZone,
      });

      if (timeBoundariesError) return timeBoundariesError;

      if (!date) return 'Дата не указана';

      const [fromHours, fromMinutes] = splitTime(from);
      const [toHours, toMinutes] = splitTime(to);

      const fromDate = +getDateTime({
        date: new Date(date),
        hours: fromHours,
        minutes: fromMinutes,
        seconds: 0,
        milliseconds: 0,
        timezoneOffset: town?.timeZone,
      });
      const toDate = +getDateTime({
        date: new Date(date),
        hours: toHours,
        minutes: toMinutes,
        seconds: 0,
        milliseconds: 0,
        timezoneOffset: town?.timeZone,
      });

      for (const booking of bookedPeriods) {
        if (
          fromDate < +new Date(booking.bookedTo) &&
          +new Date(booking.bookedFrom) < toDate
        ) {
          return 'Уже есть бронь на это время';
        }
      }

      return '';
    },
    [bookedPeriods, date, settingsFrom, town?.timeZone],
  );

  return (
    <>
      <Section
        title='Данные комнаты'
        testId='roomDataSection'
        containerClassName={classes.section}
      >
        <Typography variant='body3' data-testid='roomOffice'>
          {bookingData}
        </Typography>
        {seatCount && (
          <BookingObjectAttribute testId='roomSeatCount' iconName={Icons.User}>
            {seatCount}
          </BookingObjectAttribute>
        )}
        {room?.withFlipChart && (
          <BookingObjectAttribute testId='roomWithFlipChart'>
            Флип-чарт
          </BookingObjectAttribute>
        )}
        {room?.withTv && (
          <BookingObjectAttribute testId='roomWithTv'>
            TV
          </BookingObjectAttribute>
        )}
        {room?.withVideoCalls && (
          <BookingObjectAttribute testId='roomWithVideoCalls'>
            ВКС
          </BookingObjectAttribute>
        )}
        {room?.withWater && (
          <BookingObjectAttribute testId='roomWithWater'>
            Кулер
          </BookingObjectAttribute>
        )}
      </Section>

      <Section
        title='Данные бронирования'
        testId='roomBookForSection'
        containerClassName={clsx(classes.data, classes.section)}
      >
        {permissions.has(EmployeePermission.BookForOthers) && (
          <FormPersonAutocomplete
            type='select'
            name='bookedFor'
            label='ФИО'
            dataTestId='roomBookFor'
          />
        )}
        <FormMaskField
          key={periodskey}
          name='time'
          label='Время'
          startAdornment={<Icon iconName={Icons.Clock} />}
          maskConfig={mask}
          placeholder='чч:мм—чч:мм'
          validate={validateTime}
          fullWidth
          required
        />
      </Section>

      <Table />
    </>
  );
};
